/** @jsxImportSource preact */
import { useState } from "preact/hooks";

import * as copy from "../../paraglide/messages.js";
import { CARD_STYLES } from "../form/card-styles";
import { FORM_STYLES, formVars } from "../form/form-styles";
import { ArrowRightIcon, CheckIcon, InfoIcon } from "../icons";
import type { WidgetCartReceipt } from "../message";
import type { WidgetTokens } from "../theme";
import { CART_RECEIPT_STYLES } from "./cart-styles";

/** 回执对应哪一行购物车：改完数量后新回执顶替旧的，焦点按它找回「Edit quantity」。 */
export function cartLineId(receipt: WidgetCartReceipt): string {
  return receipt.lineKey ?? String(receipt.variantId);
}

export interface CartReceiptProps {
  receipt: WidgetCartReceipt;
  tokens: WidgetTokens;
  /** 购物车页地址；没有店面（回放、预览）就不摆两个按钮。 */
  cartHref?: string;
  onEdit?: (trigger: HTMLElement) => void;
  /** 有写入正在进行：先不让再开改数量。 */
  editDisabled?: boolean;
}

/** Shopify 加得比买家要的少、或改到一半停住时，说清楚要了几件、实际是几件。 */
function shortNote(receipt: WidgetCartReceipt): string | undefined {
  if (receipt.kind === "updated")
    return receipt.quantity < receipt.requested
      ? copy.widget_cart_receipt_short({
          requested: receipt.requested,
          count: receipt.quantity,
        })
      : undefined;
  const added = receipt.added ?? receipt.quantity;
  if (added >= receipt.requested) return undefined;
  return receipt.quantity > added
    ? copy.widget_cart_receipt_short_more({
        requested: receipt.requested,
        count: receipt.quantity,
      })
    : copy.widget_cart_receipt_short({
        requested: receipt.requested,
        count: receipt.quantity,
      });
}

/**
 * 加购回执：只在 Shopify 确认之后出现，件数是 Shopify 返回的数。
 * 同一行加过不止一次时写明这次加了几件、车里一共几件。
 */
export function CartReceipt({
  receipt,
  tokens,
  cartHref,
  onEdit,
  editDisabled = false,
}: CartReceiptProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const added = receipt.added ?? receipt.quantity;
  const short = shortNote(receipt);
  const headline =
    receipt.kind === "updated"
      ? copy.widget_cart_receipt_updated()
      : added < receipt.requested
        ? copy.widget_cart_receipt_partial({
            added,
            requested: receipt.requested,
          })
        : copy.widget_cart_receipt_added();
  const merged =
    receipt.kind === "added" && short === undefined && receipt.quantity > added;
  return (
    <div
      class="gn-card gn-cart-receipt"
      style={{ ...formVars(tokens), "--gn-cart-success": tokens.successText }}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}${CART_RECEIPT_STYLES}`}</style>
      <div class="gn-cart-receipt-head" role="status">
        <span aria-hidden="true" class="gn-cart-receipt-check">
          <CheckIcon size={12} strokeWidth={3} />
        </span>
        {headline}
      </div>
      <div class="gn-well gn-cart-receipt-line">
        <div class="gn-cart-receipt-thumb">
          {receipt.imageUrl !== undefined && !imageFailed && (
            <img
              alt=""
              onError={() => {
                setImageFailed(true);
              }}
              src={receipt.imageUrl}
            />
          )}
        </div>
        <div class="gn-cart-receipt-name">
          <div class="gn-cart-receipt-title">{receipt.title}</div>
          {receipt.variant !== undefined && (
            <div class="gn-cart-receipt-variant">{receipt.variant}</div>
          )}
        </div>
        <div class="gn-cart-receipt-qty">
          {copy.widget_cart_receipt_qty({ count: receipt.quantity })}
        </div>
      </div>
      {merged && (
        <p class="gn-cart-receipt-note">
          {copy.widget_cart_receipt_merged({
            added,
            count: receipt.quantity,
          })}
        </p>
      )}
      {short !== undefined && (
        <p class="gn-cart-receipt-note">
          <InfoIcon size={14} />
          <span>{short}</span>
        </p>
      )}
      {cartHref !== undefined && (
        <div class="gn-cart-receipt-actions">
          {onEdit !== undefined && (
            <button
              aria-haspopup="dialog"
              class="gn-btn"
              data-cart-edit={cartLineId(receipt)}
              data-compact=""
              data-disabled={editDisabled || undefined}
              data-variant="secondary"
              disabled={editDisabled}
              onClick={(event) => {
                onEdit(event.currentTarget);
              }}
              type="button"
            >
              {copy.widget_cart_edit_title()}
            </button>
          )}
          <a
            class="gn-btn"
            data-compact=""
            data-variant="secondary"
            href={cartHref}
          >
            {copy.widget_product_cart()}
            <ArrowRightIcon size={13} />
          </a>
        </div>
      )}
    </div>
  );
}
